import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Checkbox, Form, Input, Modal, Select,
} from 'antd';
import csrfHeaders from '../../utils/csrfHeaders';
import { captureAnalytics } from '../../actions/preload';
import './referringProviderRequestModal.scss';

// ENG-3658: when the referring provider isn't in the EHR, email the practice's
// support desk a "New Referring Provider Addition Request" laid out like their
// own form. The server adds Source of Information and Action Requested, picks the
// recipient, and copies the requester.
const REQUEST_PATH = '/api/v1/referring_provider_requests';
const SEND_ERROR = 'Could not send the request. Please try again.';
const NPI_SEARCH = /^\d{10}$/;

// [name, label, required, kind]; the order of the practice's form.
export const FIELDS = [
  ['provider_first_name', 'Provider First Name', true],
  ['provider_last_name', 'Provider Last Name', true],
  ['nextgen_person_number', 'NextGen Person Number', true],
  ['practice_name', 'Entity or Practice Name', true],
  ['request_type', 'Type', true, 'select'],
  ['specialty', 'Specialty', true],
  ['degree', 'Degree', true],
  ['npi', 'NPI', true],
  ['practice_contact', 'Practice Contact Information (Address/City/State/Zip Code)', true, 'textarea'],
  ['phone_number', 'Phone Number', true],
  ['fax_number', 'Fax Number', true],
  ['other_details', 'Other Details', false, 'textarea'],
];
const WIDE_FIELDS = ['practice_contact', 'other_details'];
const LABELS = Object.fromEntries(FIELDS.map(([name, label]) => [name, label]));

// What the user typed in the provider search, as a starting point when the
// document gave no name: "Okafor", "Lee Okafor", "Okafor, Lee" or an NPI.
export const valuesFromSearch = (searchText) => {
  const text = (searchText || '').trim();
  if (!text) return {};
  if (NPI_SEARCH.test(text)) return { npi: text };
  if (text.includes(',')) {
    const [last, first] = text.split(',').map((part) => part.trim());
    return { provider_last_name: last, provider_first_name: first };
  }
  const words = text.split(/\s+/);
  if (words.length === 1) return { provider_last_name: words[0] };
  return { provider_first_name: words[0], provider_last_name: words.slice(1).join(' ') };
};

// The document's own extraction wins over a search fragment; a person number
// comes from the patient selected in the drawer.
export const initialValues = ({ prefill, searchText, personNumber }) => {
  const fromSearch = valuesFromSearch(searchText);
  const values = { ...fromSearch, ...(prefill || {}) };
  if (fromSearch.npi) values.npi = fromSearch.npi;
  if (personNumber) values.nextgen_person_number = String(personNumber);
  return values;
};

const ReferringProviderRequestModal = ({
  visible, credentialId, config, searchText, personNumber, workflow, canMarkAwaitingProvider, onSent, onClose,
}) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // ENG-3659: set the document aside while the provider is added. On by default.
  const [markAwaitingProvider, setMarkAwaitingProvider] = useState(true);
  // A double click must not send two emails.
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    form.setFieldsValue(initialValues({ prefill: config?.prefill, searchText, personNumber }));
    setError(null);
    setMarkAwaitingProvider(true);
    // Only on opening: the prefill must not overwrite what the user has typed.
  }, [visible]);

  const handleSend = async () => {
    // Claimed before validating: a second click during validation would
    // otherwise pass this check too.
    if (sendingRef.current) return;
    sendingRef.current = true;
    let values;
    try {
      values = await form.validateFields();
    } catch {
      sendingRef.current = false;
      return;
    }

    setSending(true);
    setError(null);
    try {
      await axios.post(
        REQUEST_PATH,
        { ...values, emr_integration_credential_id: credentialId },
        { headers: csrfHeaders() },
      );
      const markAfterSending = canMarkAwaitingProvider && markAwaitingProvider;
      captureAnalytics('referring_provider_request_sent', {
        workflow, request_type: values.request_type, mark_awaiting_provider: markAfterSending,
      });
      onSent({
        markAwaitingProvider: markAfterSending,
        // ENG-3934: the hold records who was asked for, to match the nightly sync.
        requestedProvider: {
          npi: String(values.npi || '').replace(/\D/g, ''),
          first_name: values.provider_first_name,
          last_name: values.provider_last_name,
        },
      });
      onClose();
    } catch (requestError) {
      const fieldErrors = requestError?.response?.status === 422 ? requestError.response.data?.errors : null;
      if (fieldErrors) {
        // The server's messages are attribute-relative ("must be 10 digits").
        form.setFields(Object.entries(fieldErrors).map(([name, errors]) => ({
          name,
          errors: errors.map((detail) => `${LABELS[name] || name} ${detail}`),
        })));
      } else {
        setError(SEND_ERROR);
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  // The X, Escape and the mask all land here. Closing mid-send would let that
  // send's success close the form if it were reopened before it finished.
  const handleCancel = () => {
    if (sendingRef.current) return;
    captureAnalytics('referring_provider_request_cancelled', { workflow });
    onClose();
  };

  const renderInput = (name, kind) => {
    if (kind === 'select') {
      return (
        <Select
          placeholder="Select a type"
          getPopupContainer={(trigger) => trigger.parentNode}
          options={(config?.type_options || []).map((option) => ({ value: option, label: option }))}
        />
      );
    }
    if (kind === 'textarea') return <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />;
    return <Input />;
  };

  const requester = config?.requester;

  return (
    <Modal
      title="Request a new referring provider"
      visible={visible}
      onOk={handleSend}
      onCancel={handleCancel}
      okText="Send request"
      okButtonProps={{ loading: sending }}
      cancelButtonProps={{ disabled: sending }}
      closable={!sending}
      keyboard={!sending}
      maskClosable={!sending}
      width={640}
      zIndex={2100}
      destroyOnClose
    >
      <div className="referring-provider-request">
        <p className="referring-provider-request__intro">
          Emails the practice&apos;s support desk to add this provider. Once they add it, the provider can be
          picked here the next day. You&apos;ll get a copy, and replies come to you.
        </p>
        <dl className="referring-provider-request__fixed">
          <dt>Requester</dt>
          <dd>{requester ? `${requester.name} (${requester.email})` : ''}</dd>
          <dt>Source of Information</dt>
          <dd>Medsender</dd>
          <dt>Action Requested</dt>
          <dd>New</dd>
        </dl>
        {error && <Alert type="error" showIcon message={error} className="referring-provider-request__error" />}
        <Form form={form} layout="vertical" requiredMark className="referring-provider-request__form">
          {FIELDS.map(([name, label, required, kind]) => (
            <Form.Item
              key={name}
              name={name}
              label={label}
              className={WIDE_FIELDS.includes(name) ? 'referring-provider-request__wide' : undefined}
              rules={required ? [{ required: true, whitespace: true, message: `${label} is required` }] : []}
            >
              {renderInput(name, kind)}
            </Form.Item>
          ))}
        </Form>
        {canMarkAwaitingProvider && (
          <div className="referring-provider-request__awaiting-provider">
            <Checkbox
              checked={markAwaitingProvider}
              disabled={sending}
              onChange={(event) => setMarkAwaitingProvider(event.target.checked)}
            >
              Mark this document Awaiting Provider
            </Checkbox>
            <p className="referring-provider-request__awaiting-provider-help">
              Sets it aside without filing it. Find it with the Awaiting Provider filter once the provider is added.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

ReferringProviderRequestModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  config: PropTypes.shape({
    type_options: PropTypes.arrayOf(PropTypes.string),
    requester: PropTypes.shape({ name: PropTypes.string, email: PropTypes.string }),
    prefill: PropTypes.objectOf(PropTypes.string),
  }),
  searchText: PropTypes.string,
  personNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  workflow: PropTypes.string,
  canMarkAwaitingProvider: PropTypes.bool,
  onSent: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ReferringProviderRequestModal.defaultProps = {
  credentialId: undefined,
  config: null,
  searchText: '',
  personNumber: undefined,
  workflow: 'send_to_chart',
  canMarkAwaitingProvider: false,
  onSent: () => {},
};

export default ReferringProviderRequestModal;
